import { fmtCompactEur, fmtMonths } from '../formatters.js';

const RAISE_AMOUNT = 4_000_000;

function sum(rows, key) {
  return rows.reduce((acc, row) => acc + (row[key] ?? 0), 0);
}

export default function ProfitabilityBridge({ rows, summary, raiseAmount = RAISE_AMOUNT }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const y3Rows = safeRows.slice(24, 36);
  const last = safeRows[safeRows.length - 1] || {};
  const endingCash = raiseAmount + (summary?.cumCashEnd ?? 0);
  const y3Profit = sum(y3Rows, 'operatingProfit');
  const y3B2B =
    sum(y3Rows, 'b2bPartnerCampaignsRev') +
    sum(y3Rows, 'b2bClosedLoopRev') +
    sum(y3Rows, 'b2bWhiteLabelRev');

  const cards = [
    {
      label: 'Raise',
      value: fmtCompactEur(raiseAmount),
      sub: 'Funds growth, product, cards, and partner campaigns',
    },
    {
      label: 'Year 3 operating profit',
      value: fmtCompactEur(y3Profit),
      sub: `${fmtCompactEur(y3B2B)} from partner/payment lines`,
      good: y3Profit >= 0,
    },
    {
      label: 'Month 36 profit',
      value: fmtCompactEur(last.operatingProfit ?? 0),
      sub: `Break-even ${fmtMonths(summary?.operatingBreakEvenMonth)}`,
      good: (last.operatingProfit ?? 0) >= 0,
    },
    {
      label: 'Ending cash after raise',
      value: fmtCompactEur(endingCash),
      sub: 'Post-raise cash balance at month 36',
      good: endingCash >= 0,
    },
  ];

  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="caption text-carbon-400">Profitability bridge</p>
          <h2 className="h3 mt-2">$4M raise mapped to operating milestones.</h2>
        </div>
        <p className="label text-carbon-500 max-w-md">
          The bridge anchors the model on the round size, Year 3 operating profit, and month-36
          post-raise cash position.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mt-7">
        {cards.map((card) => (
          <div key={card.label} className="border-t border-carbon-100 pt-4">
            <p className="caption text-carbon-400">{card.label}</p>
            <p
              className={`text-2xl font-semibold tracking-h2 num-tabular mt-2 ${
                card.good === false ? 'text-rose-600' : 'text-carbon-900'
              }`}
            >
              {card.value}
            </p>
            <p className="label text-carbon-500 mt-2">{card.sub}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
